/* Diesel Proof — adds vehicle fitment to the product JSON-LD.

   Fitment lives in the SearchAuto YMM backend, not in Shopify, so Liquid
   can't put it in the server-rendered schema. This pulls the same feed the
   fitment table uses, collapses per-submodel rows into year ranges per
   make / model / engine, and writes them to the Product (or ProductGroup)
   node as isAccessoryOrSparePartFor → Vehicle.

   Google renders JavaScript and reads JSON-LD added this way. Crawlers that
   don't execute JS won't see fitment. Universal parts get no vehicle list. */
(() => {
  const script = document.getElementById('dp-product-jsonld');
  if (!script) return;

  const productId = script.dataset.productId;
  const api = script.dataset.fitmentApi;
  if (!productId || !api) return;

  const MAX_VEHICLES = 150;

  const clean = (value) => (value == null ? '' : String(value).trim());

  const engineName = (liter, cylinders) => {
    let name = clean(liter);
    if (/^\d+(\.\d+)?$/.test(name)) name += 'L';
    const cyl = clean(cylinders);
    if (cyl) name += (name ? ' ' : '') + (/^\d+$/.test(cyl) ? `${cyl} Cyl` : cyl);
    return name;
  };

  const collapse = (rows) => {
    const groups = new Map();

    rows.forEach((row) => {
      if (!row || clean(row.is_universal) === '1') return;

      const make = clean(row.make);
      const model = clean(row.model);
      if (!make || !model) return;

      const engine = engineName(row.liter, row.cylinders);
      const from = parseInt(row.from_year, 10);
      const to = parseInt(row.to_year, 10) || from;
      if (!from) return;

      const key = [make, model, engine].join('|').toLowerCase();
      const group = groups.get(key) || { make, model, engine, from, to, submodels: new Set() };
      group.from = Math.min(group.from, from);
      group.to = Math.max(group.to, to);
      if (clean(row.submodel)) group.submodels.add(clean(row.submodel));
      groups.set(key, group);
    });

    return Array.from(groups.values())
      .sort((a, b) => a.make.localeCompare(b.make) || a.model.localeCompare(b.model) || a.from - b.from)
      .slice(0, MAX_VEHICLES)
      .map((g) => {
        const years = g.from === g.to ? `${g.from}` : `${g.from}-${g.to}`;
        const vehicle = {
          '@type': 'Vehicle',
          name: [years, g.make, g.model, g.engine].filter(Boolean).join(' '),
          brand: { '@type': 'Brand', name: g.make },
          model: g.model,
        };
        if (g.from === g.to) vehicle.vehicleModelDate = String(g.from);
        if (g.engine) vehicle.vehicleEngine = { '@type': 'EngineSpecification', name: g.engine };
        if (g.submodels.size) vehicle.vehicleConfiguration = Array.from(g.submodels).join(', ');
        return vehicle;
      });
  };

  const run = () => {
    const url = `${api}${api.includes('?') ? '&' : '?'}productID=${encodeURIComponent(productId)}`;

    fetch(url)
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.json();
      })
      .then((payload) => {
        const rows = payload && Array.isArray(payload.fitmentData) ? payload.fitmentData : [];
        const vehicles = collapse(rows);
        if (!vehicles.length) return;

        const data = JSON.parse(script.textContent);
        data.isAccessoryOrSparePartFor = vehicles;
        script.textContent = JSON.stringify(data);
      })
      .catch((error) => console.warn('[dp-schema] fitment not added', error));
  };

  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(run, { timeout: 3000 });
  } else {
    window.setTimeout(run, 1500);
  }
})();
